import { Card, Select, Skeleton } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import CustomHeader from "../common/Header";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const { Option } = Select;

const supportProjects = [
    { id: 10067, name: "Aavgo Support And Operations" },
    { id: 10087, name: "Security Support" },
];

// ── Palette ──────────────────────────────────────────
const COLORS = [
    "var(--color-primary)",
    "var(--color-accent)",
    "var(--color-warning)",
    "var(--color-primary-hover)",
    "var(--color-success)",
    "var(--color-error)",
];

// ── Data transform (same as CheckInAnalytics) ────────
function transformData(rawData) {
    if (!rawData || rawData.length < 2) return { data: [], keys: [], xKey: "" };
    const [headers, ...rows] = rawData;
    const [xKey, ...seriesKeys] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: String(row[0]) };
        seriesKeys.forEach((key, i) => {
            obj[key] = Number(row[i + 1]) || 0;
        });
        return obj;
    });
    return { data, keys: seriesKeys, xKey };
}

// ── Shared tooltip ────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                color: "var(--color-text)",
            }}
        >
            <p
                style={{
                    margin: 0,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "var(--color-text-secondary)",
                }}
            >
                {label}
            </p>
            {payload.map((entry) => (
                <p
                    key={entry.name}
                    style={{
                        margin: "2px 0",
                        color: entry.color || entry.fill,
                    }}
                >
                    {entry.name}:{" "}
                    <strong style={{ color: "var(--color-text)" }}>
                        {entry.value}
                    </strong>
                </p>
            ))}
        </div>
    );
};

// ── Shared axis / grid props ──────────────────────────
const axisProps = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};
const gridProps = {
    strokeDasharray: "3 3",
    stroke: "var(--color-border-light)",
    vertical: false,
};

// ── Section wrapper card ──────────────────────────────
function ChartSection({ title, children, extra }) {
    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
                marginBottom: 20,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 16,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                        style={{
                            width: 3,
                            height: 18,
                            borderRadius: 2,
                            background:
                                "var(--gradient-primary)",
                        }}
                    />
                    <span
                        style={{
                            color: "var(--color-text)",
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        {title}
                    </span>
                </div>
                {extra}
            </div>
            {children}
        </div>
    );
}

// ── Column (Vertical Bar) chart ───────────────────────
function ColumnChart({ rawData, height = 320 }) {
    const { data, keys, xKey } = transformData(rawData);
    if (!data.length) return <ChartSkeleton />;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                data={data}
                margin={{ top: 5, right: 16, left: 0, bottom: 5 }}
                barCategoryGap="35%"
            >
                <CartesianGrid {...gridProps} />
                <XAxis dataKey={xKey} {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "var(--color-primary-light)" }}
                />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={(v) => (
                        <span style={{ color: "var(--color-text-secondary)" }}>
                            {v}
                        </span>
                    )}
                />
                {keys.map((key, i) => (
                    <Bar
                        key={key}
                        dataKey={key}
                        fill={COLORS[i % COLORS.length]}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={56}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

// ── Horizontal Bar chart ──────────────────────────────
function HorizontalBarChart({ rawData, height = 360 }) {
    const { data, keys, xKey } = transformData(rawData);
    if (!data.length) return <ChartSkeleton />;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 5, right: 16, left: 80, bottom: 5 }}
                barCategoryGap="30%"
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-light)"
                    horizontal={false}
                />
                <XAxis type="number" {...axisProps} allowDecimals={false} />
                <YAxis
                    type="category"
                    dataKey={xKey}
                    {...axisProps}
                    width={76}
                    tick={{ fill: "var(--color-text-muted)", fontSize: 11 }}
                />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "var(--color-primary-light)" }}
                />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={(v) => (
                        <span style={{ color: "var(--color-text-secondary)" }}>
                            {v}
                        </span>
                    )}
                />
                {keys.map((key, i) => (
                    <Bar
                        key={key}
                        dataKey={key}
                        fill={COLORS[i % COLORS.length]}
                        radius={[0, 4, 4, 0]}
                        maxBarSize={40}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

// ── Line chart ────────────────────────────────────────
function SupportLineChart({ rawData, height = 300 }) {
    const { data, keys, xKey } = transformData(rawData);
    if (!data.length) return <ChartSkeleton />;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={data}
                margin={{ top: 5, right: 16, left: 0, bottom: 5 }}
            >
                <CartesianGrid {...gridProps} />
                <XAxis dataKey={xKey} {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={(v) => (
                        <span style={{ color: "var(--color-text-secondary)" }}>
                            {v}
                        </span>
                    )}
                />
                {keys.map((key, i) => (
                    <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={COLORS[i % COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5 }}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

// ── Loading skeleton ──────────────────────────────────
function ChartSkeleton() {
    return (
        <Skeleton active title={false} paragraph={{ rows: 8, width: "100%" }} />
    );
}

function isReady(status) {
    return status !== "loading" && status !== "idle";
}

// ── Main component ────────────────────────────────────
function SupportAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;
    const [formData, setFormData] = useState({
        staff_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
    });
    const [selectedProject, setSelectedProject] = useState(
        supportProjects[0].id,
    );

    const mkQuery = (key, url, extra = {}) =>
        useQuery(key, async () => await HTTP(url, { ...formData, ...extra }), {
            enabled: false,
        });

    const {
        status: escalatedSupportStatus,
        data: escalatedSupportRes,
        refetch: getEscalatedUSSupport,
    } = mkQuery("escalatedSupportData", "/analytics/getEscalatedUSSupportData");
    const {
        status: escalatedTechSupportStatus,
        data: escalatedTechSupportRes,
        refetch: getEscalatedTechSupport,
    } = mkQuery(
        "escalatedTechSupportData",
        "/analytics/getEscalatedTechSupportData",
    );
    const {
        status: issueTypeSupportStatus,
        data: issueTypeSupportRes,
        refetch: getIssueTypeSupport,
    } = mkQuery("issueTypeSupportData", "/analytics/getIssueTypeSupportData");
    const {
        status: breachedResponseStatus,
        data: breachedResponseRes,
        refetch: getBreachedResponse,
    } = mkQuery(
        "breachedTimeToReponseSupportData",
        "/analytics/getBreachedTimeToReponseSupportData",
    );
    const {
        status: breachedResolutionStatus,
        data: breachedResolutionRes,
        refetch: getBreachedResolution,
    } = mkQuery(
        "breachedTimeToResolutionSupportData",
        "/analytics/getBreachedTimeToResolutionSupportData",
    );
    const {
        status: assigneeTicketStatus,
        data: assigneeTicketsRes,
        refetch: getAssigneeTickets,
    } = useQuery(
        ["support-assignee-tickets", selectedProject],
        async () =>
            await HTTP("/analytics/getSupportProjectAssigneeTickets", {
                ...formData,
                project_id: selectedProject,
            }),
        { enabled: false },
    );
    const {
        status: shiftWiseStatus,
        data: shiftWiseRes,
        refetch: getShiftWise,
    } = mkQuery("shiftWiseTicket", "/analytics/getShiftWiseTicketsData");

    const refetchAll = () => {
        getEscalatedUSSupport();
        getEscalatedTechSupport();
        getIssueTypeSupport();
        getBreachedResponse();
        getBreachedResolution();
        getAssigneeTickets();
        getShiftWise();
    };

    useEffect(() => {
        refetchAll();
    }, []);
    useEffect(() => {
        refetchAll();
    }, [formData]);
    useEffect(() => {
        getAssigneeTickets();
    }, [selectedProject]);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((prev) => ({
            ...prev,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    return (
        <div className="dashboard-container">
            <CustomHeader />

            <div
                style={{
                    padding: "16px 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                }}
            >
                {/* Row 1: Escalated Support + Tech Support */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 20,
                        marginBottom: 20,
                    }}
                >
                    <ChartSection title="Escalated Support">
                        {isReady(escalatedSupportStatus) ? (
                            <ColumnChart rawData={escalatedSupportRes?.data} />
                        ) : (
                            <ChartSkeleton />
                        )}
                    </ChartSection>
                    <ChartSection title="Escalated Tech Support">
                        {isReady(escalatedTechSupportStatus) ? (
                            <ColumnChart
                                rawData={escalatedTechSupportRes?.data}
                            />
                        ) : (
                            <ChartSkeleton />
                        )}
                    </ChartSection>
                </div>

                {/* Row 2: Issue Type (full width) */}
                <ChartSection title="Issue Type Breakdown">
                    {isReady(issueTypeSupportStatus) ? (
                        <ColumnChart
                            rawData={issueTypeSupportRes?.data}
                            height={300}
                        />
                    ) : (
                        <ChartSkeleton />
                    )}
                </ChartSection>

                {/* Row 3: Breached Response + Resolution */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 20,
                        marginBottom: 20,
                    }}
                >
                    <ChartSection title="Breached Response Time">
                        {isReady(breachedResponseStatus) ? (
                            <SupportLineChart
                                rawData={breachedResponseRes?.data}
                            />
                        ) : (
                            <ChartSkeleton />
                        )}
                    </ChartSection>
                    <ChartSection title="Breached Resolution Time">
                        {isReady(breachedResolutionStatus) ? (
                            <SupportLineChart
                                rawData={breachedResolutionRes?.data}
                            />
                        ) : (
                            <ChartSkeleton />
                        )}
                    </ChartSection>
                </div>

                {/* Row 4: Assignee Tickets (with project selector) */}
                <ChartSection
                    title="Support Project Tickets by Assignee"
                    extra={
                        <Select
                            value={selectedProject}
                            onChange={(id) => setSelectedProject(id)}
                            style={{ width: 240 }}
                        >
                            {supportProjects.map(({ id, name }) => (
                                <Option key={id} value={id}>
                                    {name}
                                </Option>
                            ))}
                        </Select>
                    }
                >
                    {isReady(assigneeTicketStatus) ? (
                        <HorizontalBarChart
                            rawData={assigneeTicketsRes?.data}
                            height={400}
                        />
                    ) : (
                        <ChartSkeleton />
                    )}
                </ChartSection>

                {/* Row 5: Shift Wise Tickets */}
                <ChartSection title="Shift Wise Tickets">
                    {isReady(shiftWiseStatus) ? (
                        <HorizontalBarChart
                            rawData={shiftWiseRes?.data}
                            height={300}
                        />
                    ) : (
                        <ChartSkeleton />
                    )}
                </ChartSection>
            </div>
        </div>
    );
}

export default SupportAnalytics;
